import React, { useMemo, useState, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceDot, ResponsiveContainer
} from 'recharts';

const getCompassDir = (deg) => {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16];
};
const kmhToKts = (v) => Math.round(v * 0.539957);

function CompassArrow({ degrees, color, size = 44 }) {
  const cx = size / 2, cy = size / 2, r = size / 2 - 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="#F5F2ED" stroke="#E5E0D8" strokeWidth="1.5" />
      <text x={cx} y={6}  textAnchor="middle" fontSize="6" fill="#8A9BA8" fontWeight="600">N</text>
      <text x={cx} y={size - 1} textAnchor="middle" fontSize="6" fill="#8A9BA8" fontWeight="600">S</text>
      <text x={size - 1} y={cy + 2} textAnchor="middle" fontSize="6" fill="#8A9BA8" fontWeight="600">E</text>
      <text x={2}  y={cy + 2} textAnchor="middle" fontSize="6" fill="#8A9BA8" fontWeight="600">W</text>
      <g transform={`rotate(${(degrees + 180) % 360}, ${cx}, ${cy})`}>
        <polygon points={`${cx},${cy - r + 4} ${cx - 3},${cy - 3} ${cx},${cy - 6} ${cx + 3},${cy - 3}`} fill={color} />
        <line x1={cx} y1={cy - 6} x2={cx} y2={cy + r - 4} stroke={color} strokeWidth="1.5" strokeOpacity="0.35" />
      </g>
    </svg>
  );
}

const timeToHours = (t) => { const [h, m] = t.split(':').map(Number); return h + m / 60; };
const hoursToTime = (h) => { const hh = Math.floor(h); const mm = Math.round((h - hh) * 60); return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`; };

const cosineInterpolate = (t, t0, h0, t1, h1) => {
  if (t1 <= t0) return h0;
  const ratio = Math.max(0, Math.min(1, (t - t0) / (t1 - t0)));
  return h0 + (h1 - h0) * (1 - Math.cos(Math.PI * ratio)) / 2;
};

const buildCurveData = (tideInfo, off = 0) => {
  if (!tideInfo) return [];
  const allTides = tideInfo.tides || [...(tideInfo.high_tides || []), ...(tideInfo.low_tides || [])];
  if (!allTides.length) return [];
  const points = allTides.map(t => ({ hour: timeToHours(t.time) + off, height: parseFloat(t.height) }));
  const seen = new Set();
  const unique = points.filter(p => { const k = `${p.hour.toFixed(2)}`; if (seen.has(k)) return false; seen.add(k); return true; });
  unique.sort((a, b) => a.hour - b.hour);
  if (unique.length < 2) return unique;

  const curve = [];
  const startH = Math.max(0, unique[0].hour - 1.5);
  const endH = Math.min(26, unique[unique.length - 1].hour + 1.5);
  for (let h = startH; h <= endH; h += 6 / 60) {
    let height;
    if (h <= unique[0].hour) height = unique[0].height;
    else if (h >= unique[unique.length - 1].hour) height = unique[unique.length - 1].height;
    else {
      let idx = 0;
      for (let i = 0; i < unique.length - 1; i++) { if (h >= unique[i].hour && h <= unique[i + 1].hour) { idx = i; break; } }
      height = cosineInterpolate(h, unique[idx].hour, unique[idx].height, unique[idx + 1].hour, unique[idx + 1].height);
    }
    curve.push({ hour: Math.round(h * 100) / 100, height: Math.round(height * 100) / 100 });
  }
  return curve;
};

const getTideStatus = (curveData, hour) => {
  if (!curveData.length) return null;
  let closestIdx = 0, minDiff = Infinity;
  for (let i = 0; i < curveData.length; i++) { const d = Math.abs(curveData[i].hour - hour); if (d < minDiff) { minDiff = d; closestIdx = i; } }
  const next = curveData[Math.min(closestIdx + 1, curveData.length - 1)];
  const curr = curveData[closestIdx];
  if (next.height > curr.height) return 'subiendo';
  if (next.height < curr.height) return 'bajando';
  return 'estable';
};

const getHeightAtHour = (curveData, hour) => {
  if (!curveData.length) return null;
  let closest = curveData[0], minDiff = Infinity;
  for (const p of curveData) { const d = Math.abs(p.hour - hour); if (d < minDiff) { minDiff = d; closest = p; } }
  return closest.height;
};

export function getSpainOffset(dateStr) {
  if (!dateStr) return 1;
  const year = parseInt(dateStr.split('-')[0], 10);
  let d = new Date(Date.UTC(year, 2, 31));
  while (d.getUTCDay() !== 0) d = new Date(d - 86400000);
  const summerStart = new Date(Date.UTC(year, 2, d.getUTCDate(), 1));
  d = new Date(Date.UTC(year, 9, 31));
  while (d.getUTCDay() !== 0) d = new Date(d - 86400000);
  const summerEnd = new Date(Date.UTC(year, 9, d.getUTCDate(), 1));
  const check = new Date(dateStr + 'T12:00:00Z');
  return (check >= summerStart && check < summerEnd) ? 2 : 1;
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1A2B3C] text-white px-3 py-2 rounded-lg shadow-lg text-xs">
      <div className="font-semibold">{hoursToTime(Math.floor(d.hour) % 24 + (d.hour % 1))}</div>
      <div className="text-[#81B29A]">{d.height.toFixed(2)} m</div>
    </div>
  );
};

// courses: [{time:"10:00", label:"San Antolin 10:00", color:"#E07A5F"}, ...]
// windData: { speed, direction, gusts }  marineData: { height, direction, period }
export default function TideChart({ tideInfo, date, courses = [], location = '', windData, marineData }) {
  const [hoverHour, setHoverHour] = useState(null);
  const offset = useMemo(() => getSpainOffset(date), [date]);
  const curveData = useMemo(() => buildCurveData(tideInfo, offset), [tideInfo, offset]);

  // Use type field from Excel data directly
  const knownPoints = useMemo(() => {
    if (!tideInfo) return { high: [], low: [] };
    const allTides = tideInfo.tides || [...(tideInfo.high_tides || []), ...(tideInfo.low_tides || [])];
    const high = [], low = [];
    allTides.forEach(t => {
      const pt = { hour: timeToHours(t.time) + offset, height: parseFloat(t.height), time: t.time };
      if (t.type === 'pleamar') high.push(pt);
      else if (t.type === 'bajamar') low.push(pt);
      else {
        // Fallback: classify by median
        const heights = allTides.map(x => parseFloat(x.height)).sort((a, b) => a - b);
        const med = heights[Math.floor(heights.length / 2)];
        (pt.height >= med ? high : low).push(pt);
      }
    });
    return { high, low };
  }, [tideInfo, offset]);

  // Course dots on the curve
  const courseDots = useMemo(() => {
    if (!courses.length || !curveData.length) return [];
    return courses.map(c => {
      const h = timeToHours(c.time);
      const height = getHeightAtHour(curveData, h);
      return { hour: h, height, color: c.color, label: c.label };
    });
  }, [courses, curveData, offset]);

  const currentHourDecimal = useMemo(() => { const now = new Date(); return now.getUTCHours() + now.getUTCMinutes() / 60 + offset; }, [offset]);
  const activeHour = hoverHour !== null ? hoverHour : currentHourDecimal;
  const currentStatus = useMemo(() => getTideStatus(curveData, activeHour), [curveData, activeHour]);
  const currentHeight = useMemo(() => getHeightAtHour(curveData, activeHour), [curveData, activeHour]);

  const handleMouseMove = useCallback((e) => { if (e?.activePayload?.[0]) setHoverHour(e.activePayload[0].payload.hour); }, []);
  const handleMouseLeave = useCallback(() => setHoverHour(null), []);

  if (!tideInfo || !curveData.length) return null;

  const yMin = Math.floor(Math.min(...curveData.map(d => d.height)) * 2) / 2;
  const yMax = Math.ceil(Math.max(...curveData.map(d => d.height)) * 2) / 2 + 0.5;
  const statusColor = currentStatus === 'subiendo' ? '#81B29A' : currentStatus === 'bajando' ? '#E07A5F' : '#8A9BA8';
  const statusIcon = currentStatus === 'subiendo' ? '↑' : currentStatus === 'bajando' ? '↓' : '↔';

  const dayLabel = (() => {
    if (!date) return '';
    const d = new Date(date + 'T12:00:00Z');
    const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${days[d.getUTCDay()]}, ${d.getUTCDate()} de ${months[d.getUTCMonth()]} de ${d.getUTCFullYear()}`;
  })();

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl p-4 space-y-2" data-testid="tide-chart">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-[#1A2B3C]">Mareas{location ? ` en ${location}` : ''}</h3>
          <p className="text-xs text-[#8A9BA8] capitalize">{dayLabel}</p>
        </div>
        <div className="flex items-center gap-3">
          {currentHeight !== null && (
            <div className="text-right">
              <div className="text-xs text-[#8A9BA8]">{hoursToTime(Math.floor(activeHour) % 24 + (activeHour % 1))}</div>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold" style={{ color: statusColor }}>{statusIcon}</span>
                <span className="text-sm font-bold text-[#1A2B3C]">{currentHeight.toFixed(2)}m</span>
                <span className="text-xs font-medium capitalize" style={{ color: statusColor }}>{currentStatus}</span>
              </div>
            </div>
          )}
          {tideInfo.coefficient != null && (
            <div className="bg-[#F5F2ED] rounded-md px-2 py-1 text-center">
              <div className="text-[10px] text-[#8A9BA8]">Coef.</div>
              <div className="text-sm font-bold text-[#4A6D7C]">{Math.round(tideInfo.coefficient < 2 ? tideInfo.coefficient * 100 : tideInfo.coefficient)}</div>
            </div>
          )}
        </div>
      </div>

      {(windData || marineData) && (
        <div className="flex items-center gap-4 border-t border-[#E5E0D8] pt-2 pb-1">
          {windData && (
            <div className="flex items-center gap-2 shrink-0">
              <CompassArrow degrees={windData.direction} color="#4A6D7C" />
              <div>
                <div className="text-[8px] text-[#8A9BA8] uppercase tracking-wide font-semibold">Viento</div>
                <div className="text-base font-bold text-[#1A2B3C] leading-tight">{kmhToKts(windData.speed)} <span className="text-[10px] font-medium text-[#8A9BA8]">kts</span></div>
                <div className="text-[10px] text-[#8A9BA8]">Rachas {kmhToKts(windData.gusts)} kts</div>
                <div className="text-[10px] font-semibold text-[#4A6D7C]">{getCompassDir(windData.direction)} {Math.round(windData.direction)}°</div>
              </div>
            </div>
          )}
          {windData && marineData && <div className="w-px self-stretch bg-[#E5E0D8]" />}
          {marineData && (
            <div className="flex items-center gap-2 shrink-0">
              <CompassArrow degrees={marineData.direction} color="#81B29A" />
              <div>
                <div className="text-[8px] text-[#8A9BA8] uppercase tracking-wide font-semibold">Ola</div>
                <div className="text-base font-bold text-[#1A2B3C] leading-tight">{marineData.height?.toFixed(1)} <span className="text-[10px] font-medium text-[#8A9BA8]">m</span></div>
                <div className="text-[10px] text-[#8A9BA8]">Período {marineData.period}s</div>
                <div className="text-[10px] font-semibold text-[#81B29A]">{getCompassDir(marineData.direction)} {Math.round(marineData.direction)}°</div>
              </div>
            </div>
          )}
        </div>
      )}

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={curveData} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}
          margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <defs>
            <linearGradient id="tideGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4A6D7C" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#4A6D7C" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
          <XAxis dataKey="hour" type="number" domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => `${String(Math.floor(v) % 24).padStart(2, '0')}:00`}
            ticks={[0, 4, 8, 12, 16, 20, 24].map(t => t + offset)}
            tick={{ fontSize: 10, fill: '#8A9BA8' }} axisLine={{ stroke: '#E5E0D8' }} tickLine={false} />
          <YAxis domain={[yMin, yMax]} tickFormatter={(v) => `${v}m`}
            tick={{ fontSize: 10, fill: '#8A9BA8' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#4A6D7C', strokeWidth: 1, strokeDasharray: '4 4' }} />
          <Area type="monotone" dataKey="height" stroke="#4A6D7C" strokeWidth={2}
            fill="url(#tideGrad)" dot={false}
            activeDot={{ r: 4, fill: '#4A6D7C', stroke: '#fff', strokeWidth: 2 }} />
          {/* Pleamar markers (green) */}
          {knownPoints.high.map((p, i) => (
            <ReferenceDot key={`h${i}`} x={p.hour} y={p.height} r={5} fill="#81B29A" stroke="#fff" strokeWidth={2} />
          ))}
          {/* Bajamar markers (red) */}
          {knownPoints.low.map((p, i) => (
            <ReferenceDot key={`l${i}`} x={p.hour} y={p.height} r={5} fill="#E07A5F" stroke="#fff" strokeWidth={2} />
          ))}
          {/* Course time dots on curve */}
          {courseDots.map((cd, i) => (
            <ReferenceDot key={`c${i}`} x={cd.hour} y={cd.height} r={4} fill={cd.color} stroke="#fff" strokeWidth={1.5} />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      <div className="flex items-center justify-between text-[10px] text-[#8A9BA8] px-1">
        <span>Hora local España</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4A6D7C] inline-block" /> alturas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#81B29A] inline-block" /> pleamar</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E07A5F] inline-block" /> bajamar</span>
          {courseDots.map((cd, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: cd.color }} />
              <span className="truncate max-w-[80px]">{cd.label}</span>
            </span>
          ))}
        </div>
        <span>Fuente: IHM</span>
      </div>
    </div>
  );
}
